import { useCallback, useEffect, useState } from 'react';
import { loadTeamCards } from '../../app/coach';
import type { TeamCard } from '../../app/coach';
import { loadTeamFile, saveBoardAsTemplate } from '../../app/home';
import type { FileMatch, TeamFile } from '../../app/home';
import { newId } from '../../app/ids';
import { log } from '../../app/logger';
import { loadRoster } from '../../app/roster';
import type { OurPlayerRow } from '../../types';
import { playerLabel } from '../../domain/file';
import { CoachCardView } from '../card/CoachCardView';
import { SaveTemplateSheetB } from './SaveTemplateSheetB';

/**
 * B spec §2.4 — **the opponent file** (build unit B-6; scenarios 1, 33, 34's accumulation
 * half): everything B knows about one opponent, from the engine's existing FILE facade
 * (`loadTeamFile`, reused as-is — the U-16 read whose transaction scope is the mechanism that
 * keeps this a scouting file rather than a capture log).
 *
 * §2.4's inventory, mapped to what the facade provides:
 * - **match history vs them** — `TeamFile.matches` (`byMatchHistory` order); each row taps into
 *   the match record (B-18's surface, reached through the shell).
 * - **confirmed/invalidated patterns across matches** — `TeamFile.hypotheses` (`FileStatus` is
 *   the closed pair by design), status carried by a word + color, never hue alone.
 * - **players (shirt-number stubs)** — the ENV-11 `PlayerLink` attached to patterns, rendered
 *   through `playerLabel` (`#7 · left foot`). The facade deliberately exposes no other path to
 *   a `PlayerRow`, so the stubs live on the pattern rows — the place they mean something.
 * - **save-board-as-template for past matches** — PRE-6, `canSaveTemplate`/`boardSize` from the
 *   facade, dispatch via `saveBoardAsTemplate`. The control is absent for a running match's
 *   row (the engine refuses too — the UI hides, the domain rejection enforces).
 * - **their cards — including points-of-emphasis cards** — B-16's `CoachCardView` over
 *   `loadTeamCards`, one card per (match, context), newest match first, each paged one adjustment
 *   at a time. This is scenario 30's *"reviewable any time later"* and half of scenario 34: an
 *   opponent file that accumulates is where a points-of-emphasis card waits between the scouting
 *   that produced it and the match it is for.
 *
 * A's talk points are deliberately NOT rendered: §2.4's B inventory does not list them — B
 * wires nothing to A's distillation artifacts (the frozen path), even read-only.
 */

const MODULE = 'ui-b/opponents';

type Phase = 'loading' | 'ready' | 'missing' | 'failed';

export interface OpponentFileScreenProps {
  readonly teamId: string;
  readonly onBack: () => void;
  /** A history row's tap — the match record (B-18's surface, the shell's overlay). */
  readonly onOpenRecord: (matchId: string) => void;
}

export function OpponentFileScreen({ teamId, onBack, onOpenRecord }: OpponentFileScreenProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [file, setFile] = useState<TeamFile | null>(null);
  /** B-16 — every finalized card from every match against this opponent (§2.5, scenario 30). */
  const [cards, setCards] = useState<readonly TeamCard[]>([]);
  /**
   * §18.4 — the roster the card's NAME JOIN reads.
   *
   * Read here as well as at the break because a card must read **the same words on every surface
   * that shows it**: the file rendering `#7` while the break rendered `#7 Maya` is one card with
   * two texts, and `verify-b-card`'s *"same framing, same four pages, same words"* assertion is
   * what catches it. Cheap — a 12–20 row profile-scoped read that rides the same `Promise.all`.
   */
  const [roster, setRoster] = useState<readonly OurPlayerRow[]>([]);
  const [reads, setReads] = useState(0);
  const [saving, setSaving] = useState<FileMatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback((): void => {
    void (async () => {
      /* Two reads, one render: the file and its cards land together, so the screen never shows a
         match list from one instant beside a card list from another. */
      const [loaded, teamCards, players] = await Promise.all([
        loadTeamFile(teamId),
        loadTeamCards(teamId),
        loadRoster(),
      ]);
      setFile(loaded ?? null);
      setCards(teamCards);
      setRoster(players);
      setPhase(loaded === undefined ? 'missing' : 'ready');
      setReads((count) => count + 1);
    })().catch((cause: unknown) => {
      log.error(MODULE, 'B §2.4: the opponent file could not be read', {
        teamId,
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setPhase('failed');
    });
  }, [teamId]);

  useEffect(refresh, [refresh]);

  const save = (match: FileMatch, name: string): void => {
    setBusy(true);
    setProblem(null);
    void saveBoardAsTemplate(match.matchId, newId(), name)
      .then((result) => {
        if (!result.ok) {
          /* An engine refusal is an ordinary sentence in the sheet, never a fault banner. */
          setProblem(result.message);
          return;
        }
        setSaving(null);
        setNotice(`Saved “${name.trim()}” to your template library.`);
        refresh();
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'PRE-6: the template could not be saved', {
          matchId: match.matchId,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setProblem('The template could not be saved. The log has the detail.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (phase === 'loading') return <p className="homeb-empty">Opening the file…</p>;
  if (phase === 'failed')
    return (
      <p className="homeb-empty" role="alert">
        This opponent’s file could not be read. The log has the detail.
      </p>
    );
  if (phase === 'missing' || file === null)
    return (
      <div className="oppfile" data-testid="oppfile" data-phase="missing">
        <button type="button" className="oppfile__back" data-testid="oppfile-back" onClick={onBack}>
          ‹ Opponents
        </button>
        <p className="homeb-empty">That opponent is no longer on this device.</p>
      </div>
    );

  return (
    <div
      className="oppfile"
      data-testid="oppfile"
      data-phase="ready"
      data-team={file.teamId}
      data-read={String(reads)}
      data-matches={String(file.matches.length)}
      data-patterns={String(file.hypotheses.length)}
    >
      <header className="oppfile__top">
        <button type="button" className="oppfile__back" data-testid="oppfile-back" onClick={onBack}>
          ‹ Opponents
        </button>
        <h1 className="oppfile__title">{file.name}</h1>
        {(file.club !== null || file.ageGroup !== null) && (
          <p className="oppfile__sub">{[file.club, file.ageGroup].filter(Boolean).join(' · ')}</p>
        )}
      </header>

      {notice !== null && (
        <p className="myteam-notice" data-testid="oppfile-notice" role="status">
          {notice}
          <button
            type="button"
            className="myteam-notice__dismiss"
            onClick={() => {
              setNotice(null);
            }}
          >
            Dismiss
          </button>
        </p>
      )}

      <section className="oppfile-section">
        <h2 className="oppfile-section__title">Matches</h2>
        {file.matches.length === 0 && (
          <p className="homeb-empty" data-testid="oppfile-no-matches">
            No matches against this opponent yet.
          </p>
        )}
        <ul className="oppfile-list" data-testid="oppfile-matches">
          {file.matches.map((match) => (
            <li
              key={match.matchId}
              className="oppfile-match"
              data-testid={`oppfile-match-${match.matchId}`}
              data-can-template={match.canSaveTemplate ? 'true' : 'false'}
            >
              <button
                type="button"
                className="oppfile-match__open"
                data-testid={`oppfile-open-${match.matchId}`}
                onClick={() => {
                  onOpenRecord(match.matchId);
                }}
              >
                <span className="oppfile-match__date">{match.date}</span>
                <span className="oppfile-match__state">{match.stateLabel}</span>
                {match.comp !== null && <span className="homeb-chip">{match.comp}</span>}
              </button>
              {/* PRE-6 — rendered only inside the post-match window: a running match's row has
                  no such control in the DOM at all (the observable negative; the engine's
                  `ERR_TEMPLATE_NOT_POST_MATCH` is the enforcement behind it). */}
              {match.canSaveTemplate && (
                <button
                  type="button"
                  className="oppfile-match__template"
                  data-testid={`oppfile-template-${match.matchId}`}
                  /* A match played list-only reaches full time with no board (`boardSize` 0):
                     the control says so rather than opening a sheet that can only fail. */
                  disabled={match.boardSize === 0}
                  data-board={String(match.boardSize)}
                  onClick={() => {
                    setProblem(null);
                    setSaving(match);
                  }}
                >
                  {match.boardSize === 0
                    ? 'No board to save'
                    : `Save board as template (${String(match.boardSize)})`}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="oppfile-section">
        <h2 className="oppfile-section__title">Patterns across matches</h2>
        {file.hypotheses.length === 0 && (
          <p className="homeb-empty" data-testid="oppfile-no-patterns">
            Nothing confirmed or ruled out yet.
          </p>
        )}
        <ul className="oppfile-list" data-testid="oppfile-patterns">
          {file.hypotheses.map((row) => (
            <li
              key={row.hypothesisId}
              className="oppfile-pattern"
              data-testid={`oppfile-pattern-${row.hypothesisId}`}
              data-status={row.status}
            >
              {/* The status is a word plus a color, never the color alone (skill §3; the F-20
                  lesson — a status carried by styling is invisible in bright sun). */}
              <span className={`oppfile-pattern__status oppfile-pattern__status--${row.status}`}>
                {row.status === 'confirmed' ? 'Confirmed' : 'Ruled out'}
              </span>
              <span className="oppfile-pattern__text">{row.text}</span>
              <span className="oppfile-pattern__meta">
                {row.matchDate}
                {/* ENV-11 — a shirt-number stub; there is no other way to name a player. */}
                {row.player !== null && (
                  <span
                    className="oppfile-pattern__player"
                    data-testid={`oppfile-player-${row.hypothesisId}`}
                  >
                    {playerLabel(row.player)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* B-16 — the cards, newest match first, each paged one adjustment at a time (§2.5 step 4).
          Finalized only: a draft is unfinished business, and it belongs to the workflow still
          building it and to the Needs-finishing queue chasing it, not to a file that reads as
          history (`loadTeamCards` carries the reasoning). */}
      <section className="oppfile-section" data-testid="oppfile-cards" data-cards={String(cards.length)}>
        <h2 className="oppfile-section__title">Cards</h2>
        {cards.length === 0 ? (
          <p className="oppfile-seam" data-testid="oppfile-no-cards">
            No cards yet. Halftime adjustments, full-time reviews and points of emphasis all keep
            here once you make them.
          </p>
        ) : (
          <ul className="oppfile-cards" data-testid="oppfile-cards-list">
            {cards.map((entry) => (
              <li key={entry.card.id}>
                <CoachCardView
                  card={entry.card}
                  purpose={entry.purpose}
                  sources={entry.sources}
                  roster={roster}
                  meta={entry.matchDate}
                  testId={`oppcard-${entry.card.id}`}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {saving !== null && (
        <SaveTemplateSheetB
          opponent={file.name}
          date={saving.date}
          entryCount={saving.boardSize}
          busy={busy}
          problem={problem}
          onSave={(name) => {
            save(saving, name);
          }}
          onClose={() => {
            setSaving(null);
            setProblem(null);
          }}
        />
      )}
    </div>
  );
}
